import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import GroupedProduct from './GroupedProduct/GroupedProduct';
import InsertHtmlContent from 'components/Common/InsertHtmlContent/InsertHtmlContent';
import Trans from 'components/Common/Translation/Trans';
import Pager from 'components/Listing/Pager/Pager';
import ProductList from 'components/Listing/ProductList/ProductList';
import ProductsViewed from 'components/Listing/ProductsViewed/ProductsViewed';
import { BaseProps } from 'components/Listing/CategoryRenderTypes';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import { formatListingProducts } from 'services/gtm/inputDataFormatter';
import { pushProductClickEvent } from 'services/listing/gtm';
import { GTM_PAGE_LIST } from 'settings/gtm/gtmPageView';
import {
  BreadcrumbType,
  GroupedProductListingType,
  ProductType,
} from 'types/ProductListing/ProductListing';

import styles from './GroupedProducts.module.scss';

export default function GroupedProducts({
  data,
  isDataValidating,
}: BaseProps<GroupedProductListingType>): JSX.Element {
  const navigate = useNavigate();
  const { pathname, state, search, hash } = useLocation();
  const { banner, breadcrumb, pager, products, productCount, seo } = data;
  const displayPager = Object.keys(pager.pages).length > 1;

  function renderProduct(product: ProductType): JSX.Element {
    const { id } = product;
    return (
      <GroupedProduct
        key={id}
        product={product}
        onClick={() => {
          pushProductClickEvent(product);
          navigate(`${pathname}${search}${hash}`, {
            replace: true,
            state: { ...state, scrollToProduct: id },
          });
        }}
        scrollInto={state?.scrollToProduct === id}
      />
    );
  }

  return (
    <>
      <GtmPageView
        title={seo.title}
        breadcrumb={breadcrumb}
        products={products}
        isDataValidating={isDataValidating}
      />
      {banner && <InsertHtmlContent htmlContent={banner} />}
      <h1 className={styles.title}>{seo.titleH1}</h1>
      <span className={styles.nbProducts}>
        <Trans
          i18nKey={'NB_PRODUCTS'}
          namespace={'listingPage'}
          values={{ count: productCount.toString() }}
        />
      </span>
      <ProductList
        products={products}
        renderProduct={product => renderProduct(product)}
        className={styles.productListContainer}
      />
      <ProductsViewed count={pager.currentProductCount} total={productCount} />
      {displayPager && <Pager pager={pager} />}
    </>
  );
}

type GtmPageViewProps = {
  title: string;
  breadcrumb: BreadcrumbType[];
  products: ProductType[];
  isDataValidating: boolean;
};

function GtmPageView({
  title,
  breadcrumb,
  products,
  isDataValidating,
}: GtmPageViewProps) {
  useGtmPageView(GTM_PAGE_LIST, title, {
    breadcrumb,
    products: formatListingProducts(products),
    isLoaded: !isDataValidating,
  });

  return null;
}
